import React from 'react';
import TableRow from '@mui/material/TableRow';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import TableCell from 'src/components/Table/TableCell';
import dateFormatType from 'src/utils/dateFormatType';
import { formatPositiveAmount } from 'src/utils/formatAmout';
import selectFormatDate from 'src/utils/selectDateFormat';
import TooltipTypography from 'src/components/TooltipTypography/TooltipTypography';
import StatusBadge from 'src/components/StatusBadge';

const GlobalPaymentRow = ({
  invoiceDbId,
  folio,
  amountIvaIncluded,
  date,
  client,
  clientId,
  clientRfc,
  contractId,
  contractFolioWithoutPrefix,
  originChannel,
  relatedInvoicesCount,
  pendingAfter,
  onView
}) => {
  const { t } = useTranslation();

  const relatedInvoicesLabel =
    relatedInvoicesCount === 1
      ? `1 ${t('TRANSACTION.RELATED_INVOICE_SINGULAR')}`
      : `${relatedInvoicesCount} ${t('TRANSACTION.RELATED_INVOICE_PLURAL')}`;

  return (
    <TableRow hover>
      <TableCell align="center">
        <Typography>{selectFormatDate(date, dateFormatType.DAY)}</Typography>
      </TableCell>
      <TableCell align="center">
        {folio ? (
          <Typography
            component={Link}
            to={`/invoice/${invoiceDbId}`}
            sx={theme => ({
              color: theme.palette.secondary.edit,
              fontWeight: 800,
              textDecoration: 'none'
            })}
          >
            {folio}
          </Typography>
        ) : (
          <TooltipTypography
            title={t('TRANSACTION.NO_INVOICE_ASSOCIATED')}
            text="--"
          />
        )}
      </TableCell>
      <TableCell align="center">
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Link
            to={`/clients/detail/${clientId}/accounting`}
            style={{ textDecoration: 'none' }}
          >
            <Typography
              sx={theme => ({
                color: theme.palette.secondary.edit,
                fontWeight: 700
              })}
            >
              {client}
            </Typography>
          </Link>
          <Typography variant="caption" color="text.secondary">
            {clientRfc}
          </Typography>
        </Box>
      </TableCell>
      <TableCell align="center">
        {contractId ? (
          <Link to={`/contracts/${contractId}`}>
            <Typography
              sx={theme => ({ color: theme.palette.secondary.edit, fontWeight: 800 })}
            >
              {contractFolioWithoutPrefix}
            </Typography>
          </Link>
        ) : (
          <TooltipTypography
            title={t('TRANSACTION.NO_CONTRACT_ASSOCIATED')}
            text="--"
          />
        )}
      </TableCell>
      <TableCell align="center">
        <Typography>{t(`TRANSACTION.PAYMENT_ORIGIN.${originChannel}`)}</Typography>
      </TableCell>
      <TableCell align="center">
        <StatusBadge tone={relatedInvoicesCount > 1 ? 'info' : 'neutral'}>
          {relatedInvoicesLabel}
        </StatusBadge>
      </TableCell>
      <TableCell align="center">
        {pendingAfter > 0 ? (
          <Typography sx={{ color: 'error.main', fontWeight: 700 }}>
            {formatPositiveAmount(pendingAfter)}
          </Typography>
        ) : (
          <StatusBadge tone="success">
            {t('TRANSACTION.SETTLED_LABEL')}
          </StatusBadge>
        )}
      </TableCell>
      <TableCell align="center">
        <Typography sx={{ fontWeight: 700 }}>
          {formatPositiveAmount(amountIvaIncluded)}
        </Typography>
      </TableCell>
      <TableCell align="center">
        <IconButton
          sx={{ width: 32, height: 32, color: 'primary.main' }}
          onClick={() => onView(invoiceDbId)}
          disabled={!invoiceDbId}
          aria-label={t('TRANSACTION.DETAIL.VIEW_DETAIL')}
        >
          <VisibilityIcon fontSize="small" />
        </IconButton>
      </TableCell>
    </TableRow>
  );
};

GlobalPaymentRow.propTypes = {
  invoiceDbId: PropTypes.number,
  folio: PropTypes.string,
  amountIvaIncluded: PropTypes.number.isRequired,
  date: PropTypes.string.isRequired,
  client: PropTypes.string,
  clientId: PropTypes.number,
  clientRfc: PropTypes.string,
  contractId: PropTypes.string,
  contractFolioWithoutPrefix: PropTypes.string,
  originChannel: PropTypes.string,
  relatedInvoicesCount: PropTypes.number,
  pendingAfter: PropTypes.number,
  onView: PropTypes.func.isRequired
};

GlobalPaymentRow.defaultProps = {
  invoiceDbId: null,
  folio: null,
  client: null,
  clientId: null,
  clientRfc: null,
  contractId: null,
  contractFolioWithoutPrefix: null,
  originChannel: null,
  relatedInvoicesCount: 0,
  pendingAfter: 0
};

export default GlobalPaymentRow;
